import { useState, useEffect } from 'react';

interface RatingMatrixProps {
  versions: Array<{ id: string; name: string }>;
  dimensions: Array<{ id: string; name: string; description?: string }>;
  onSubmit: (ratings: Array<{ versionId: string; dimension: string; score: number }>) => void;
  disabled?: boolean;
}

export function RatingMatrix({ versions, dimensions, onSubmit, disabled = false }: RatingMatrixProps) {
  // 初始化评分矩阵：dimension -> version -> score
  const [ratings, setRatings] = useState<Map<string, Map<string, number>>>(new Map());
  
  useEffect(() => {
    // 初始化所有评分为 5
    const initialRatings = new Map<string, Map<string, number>>();
    dimensions.forEach(dim => {
      const versionScores = new Map<string, number>();
      versions.forEach(ver => {
        versionScores.set(ver.id, 5);
      });
      initialRatings.set(dim.id, versionScores);
    });
    setRatings(initialRatings);
  }, [versions, dimensions]);
  
  const setScore = (dimensionId: string, versionId: string, score: number) => {
    setRatings(prev => {
      const newRatings = new Map(prev);
      const dimensionScores = new Map(newRatings.get(dimensionId) || new Map());
      dimensionScores.set(versionId, score);
      newRatings.set(dimensionId, dimensionScores);
      return newRatings;
    });
  };
  
  const getScore = (dimensionId: string, versionId: string): number => {
    return ratings.get(dimensionId)?.get(versionId) || 5;
  };
  
  const handleSubmit = () => {
    const flatRatings: Array<{ versionId: string; dimension: string; score: number }> = [];
    
    ratings.forEach((versionScores, dimensionId) => {
      versionScores.forEach((score, versionId) => {
        flatRatings.push({
          versionId,
          dimension: dimensionId,
          score,
        });
      });
    });
    
    onSubmit(flatRatings);
  };
  
  // 检查是否所有评分都已完成
  const allRated = ratings.size === dimensions.length && 
    Array.from(ratings.values()).every(versionScores => versionScores.size === versions.length);
  
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <h3 className="font-bold text-lg mb-4">评分矩阵</h3>
      <p className="text-gray-600 text-sm mb-6">
        请为每个版本在各个维度上打分（1-10分，10分最高）
      </p>
      
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className="border border-gray-300 bg-gray-50 p-3 text-left font-medium">
                评估维度
              </th>
              {versions.map(version => (
                <th
                  key={version.id}
                  className="border border-gray-300 bg-gray-50 p-3 text-center font-medium"
                >
                  {version.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {dimensions.map(dimension => (
              <tr key={dimension.id}>
                <td className="border border-gray-300 p-3">
                  <div className="font-medium">{dimension.name}</div>
                  {dimension.description && (
                    <div className="text-xs text-gray-500 mt-1">
                      {dimension.description}
                    </div>
                  )}
                </td>
                {versions.map(version => (
                  <td key={version.id} className="border border-gray-300 p-3">
                    <div className="flex flex-col items-center gap-2">
                      <input
                        type="range"
                        min="1"
                        max="10"
                        value={getScore(dimension.id, version.id)}
                        onChange={(e) => setScore(dimension.id, version.id, Number(e.target.value))}
                        disabled={disabled}
                        className="w-full"
                      />
                      <div className="text-lg font-bold text-sky-600">
                        {getScore(dimension.id, version.id)}
                      </div>
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      <button
        onClick={handleSubmit}
        disabled={disabled || !allRated}
        className="w-full mt-6 bg-sky-500 hover:bg-sky-600 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        提交评分
      </button>
    </div>
  );
}

